import React, { useState } from "react";
import { Box, Button, Divider, Menu, MenuItem, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { FaQuestionCircle, FaSignOutAlt, FaUser } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../../../context/UserContext";
import { guardedNavigate } from "../../../public/components/ds/DealSessionManager";
import { runBeforeLogout } from "../../../public/lib/dealSession";

const COLORS = { ink: "#14242e", body: "#24323c", text: "#44525c", muted: "#55616b", canvas: "#f7faf8", pageZone: "#f0f5f2", green: "#0b7a55", greenTint: "#e1efe7", edge: "rgba(20,36,46,.16)", accentHover: "#aebec7", accentHoverText: "#2f4956" };
const FONT = '"IBM Plex Sans", system-ui, sans-serif';
// Matches the landing page's own dropdown exactly (public/components/ui/
// dropdown-menu.jsx's Radix trigger, e.g. the header language switcher): a
// 10px-radius sheet with a hairline edge, the DS's lifted-not-heavy shadow,
// 4px inner padding, and 7px-rounded rows that tint to the site's --accent
// colour (hsl(200 18% 73%) = #aebec7, with --accent-foreground/--slate-700
// = #2f4956 text) on hover — the same grammar wherever a dropdown opens in
// this app, authenticated header or public page alike.
const menuPaperSx = {
  mt: 1,
  minWidth: 164,
  border: `1px solid ${COLORS.edge}`,
  borderRadius: "10px",
  backgroundColor: "#fff",
  boxShadow: "0 12px 28px -18px rgba(16,30,40,.4)",
  padding: "4px",
  "& .MuiList-root": { padding: 0 },
};
const menuItemSx = {
  minHeight: 0,
  gap: 1,
  padding: "8px 10px",
  borderRadius: "7px",
  fontFamily: FONT,
  fontSize: "13.5px",
  color: COLORS.body,
  transition: "background-color 0.15s ease, color 0.15s ease",
  "&:hover": { backgroundColor: COLORS.accentHover, color: COLORS.accentHoverText },
  "&.Mui-focusVisible": { backgroundColor: COLORS.accentHover, color: COLORS.accentHoverText },
};

/** Shared authenticated header controls used by every application header. */
function DropdownProfile({ focused = false, identityName = "", hideAccount = false }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { displayName, displayInitials, loading } = useUser();
  const { t, i18n } = useTranslation();
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [languageAnchor, setLanguageAnchor] = useState(null);
  // Marks the panel's own current destination quietly, matching /profile and
  // /support (Klargo-Reference AuthShell.tsx).
  const activeMenuItemSx = { ...menuItemSx, backgroundColor: "rgba(20,36,46,.05)", fontWeight: 600, color: COLORS.ink };

  const currentLanguage = i18n.resolvedLanguage?.startsWith("sv") ? "SV" : "EN";
  const accountName = identityName.trim() || displayName || displayInitials || t("profile.menu.profile");
  const controlColor = focused ? "#f7f9f9" : COLORS.body;
  const controlHover = focused ? "rgba(255,255,255,.08)" : COLORS.canvas;
  const controlBorder = focused ? "rgba(255,255,255,.30)" : COLORS.edge;
  // Mirrors the landing page's own trigger buttons exactly (public.source.css's
  // shared `.press` class): a uniform scale-down on press, not MUI's default
  // ripple — so the button never appears to change width, only to dip
  // slightly in place.
  const sharedControlSx = {
    minHeight: 40, borderRadius: "9px", background: "transparent", color: controlColor,
    fontFamily: FONT, textTransform: "none", boxShadow: "none",
    transition: "transform 120ms cubic-bezier(0.22,1,0.36,1), background-color 120ms cubic-bezier(0.22,1,0.36,1)",
    "&:hover": { backgroundColor: controlHover, boxShadow: "none" },
    "&:active": { transform: "scale(0.985)" },
    "&:focus-visible": { outline: "2px solid #fff", outlineOffset: "1px", boxShadow: `0 0 0 4px ${COLORS.green}` },
    "@media (prefers-reduced-motion: reduce)": { transition: "background-color 120ms ease", "&:active": { transform: "none" } },
  };

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem("appLanguage", language);
    setLanguageAnchor(null);
  };
  const logout = async () => {
    // Let a connected deal's counterpart know before the session is cleared.
    await runBeforeLogout();
    localStorage.clear();
    navigate("/Home");
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: focused ? { xs: 0.5, sm: 1.5 } : { xs: 0.25, sm: 0.75 } }}>
      <Button
        type="button"
        disableRipple
        onClick={(event) => setLanguageAnchor(event.currentTarget)}
        aria-label={currentLanguage === "SV" ? "Byt språk" : "Change language"}
        aria-haspopup="menu"
        aria-expanded={Boolean(languageAnchor)}
        startIcon={<PublicOutlinedIcon sx={{ fontSize: "16px !important" }} />}
        endIcon={<ArrowDropDownIcon sx={{ fontSize: "17px !important" }} />}
        sx={{ ...sharedControlSx, minWidth: 0, px: { xs: 0.75, sm: 1 }, fontSize: "13px !important", fontWeight: 600, "& .MuiButton-startIcon": { mr: 0.5 }, "& .MuiButton-endIcon": { ml: 0.25 } }}
      >
        {currentLanguage}
      </Button>
      <Menu anchorEl={languageAnchor} open={Boolean(languageAnchor)} onClose={() => setLanguageAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} transformOrigin={{ vertical: "top", horizontal: "center" }} PaperProps={{ sx: { ...menuPaperSx, minWidth: 148 } }}>
        {currentLanguage === "SV" ? (
          <MenuItem onClick={() => changeLanguage("en")} sx={menuItemSx}>
            <Box component="span" sx={{ width: 22, fontSize: "11px", fontWeight: 600, color: COLORS.muted }}>EN</Box>English
          </MenuItem>
        ) : (
          <MenuItem onClick={() => changeLanguage("sv")} sx={menuItemSx}>
            <Box component="span" sx={{ width: 22, fontSize: "11px", fontWeight: 600, color: COLORS.muted }}>SV</Box>Svenska
          </MenuItem>
        )}
      </Menu>

      {focused && !hideAccount && (
        <Box
          aria-hidden="true"
          sx={{ width: "1px", height: 20, mx: { xs: 0, sm: 0.25 }, backgroundColor: "rgba(247,249,249,.22)" }}
        />
      )}

      {!hideAccount && (
      <>
      <Button
        type="button"
        disableRipple
        onClick={(event) => setProfileAnchor(event.currentTarget)}
        aria-label={accountName}
        aria-haspopup="menu"
        aria-expanded={Boolean(profileAnchor)}
        startIcon={focused ? undefined : <PersonOutlineIcon sx={{ fontSize: "17px !important" }} />}
        endIcon={focused ? <ArrowDropDownIcon sx={{ fontSize: "17px !important", transform: profileAnchor ? "rotate(180deg)" : "none", transition: "transform 150ms" }} /> : undefined}
        sx={{
          ...sharedControlSx,
          maxWidth: { xs: 126, sm: 220 },
          px: focused ? { xs: 0.5, sm: 1 } : { xs: 0.75, sm: 1.25 },
          border: focused ? "none" : `1px solid ${controlBorder}`,
          borderBottom: focused ? "2px solid transparent" : undefined,
          borderRadius: focused ? 0 : "9px",
          fontSize: "13px !important",
          fontWeight: focused ? 600 : 500,
          "&:hover": focused ? { backgroundColor: "transparent", borderBottomColor: "rgba(247,249,249,.4)" } : undefined,
          "& .MuiButton-startIcon": { mr: 0.75, flex: "none" },
          "& .MuiButton-endIcon": { ml: 0.35, flex: "none" },
        }}
      >
        <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {loading && !identityName ? "…" : accountName}
        </Box>
      </Button>
      <Menu anchorEl={profileAnchor} open={Boolean(profileAnchor)} onClose={() => setProfileAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} PaperProps={{ sx: { ...menuPaperSx, minWidth: focused ? 230 : 164 } }}>
        {focused && (
          <Box sx={{ px: 1.5, pt: 1.25, pb: 1.1, borderBottom: "1px solid rgba(20,36,46,.08)" }}>
            <Typography sx={{ fontFamily: FONT, fontSize: "13px", fontWeight: 600, color: COLORS.ink }}>{accountName}</Typography>
            <Typography sx={{ mt: 0.25, fontFamily: FONT, fontSize: "12px", fontWeight: 500, color: COLORS.muted }}>{t("profile.menu.account")}</Typography>
          </Box>
        )}
        <MenuItem
          onClick={() => { setProfileAnchor(null); guardedNavigate("/Profile"); }}
          sx={pathname === "/Profile" ? activeMenuItemSx : menuItemSx}
          aria-current={pathname === "/Profile" ? "page" : undefined}
        >
          {!focused && <FaUser size={13} />}{t("profile.menu.profile")}
        </MenuItem>
        <MenuItem
          onClick={() => { setProfileAnchor(null); guardedNavigate("/support"); }}
          sx={pathname === "/support" ? activeMenuItemSx : menuItemSx}
          aria-current={pathname === "/support" ? "page" : undefined}
        >
          {!focused && <FaQuestionCircle size={13} />}{t("profile.menu.helpSupport")}
        </MenuItem>
        <Divider sx={{ my: 0.5, borderColor: "rgba(20,36,46,.08)" }} />
        <MenuItem onClick={logout} sx={focused ? menuItemSx : { ...menuItemSx, color: "#8f2018", "&:hover": { backgroundColor: "#f8e9e7", color: "#8f2018" } }}>{!focused && <FaSignOutAlt size={13} />}{t("profile.menu.logout")}</MenuItem>
      </Menu>
      </>
      )}
    </Box>
  );
}

export default DropdownProfile;
